import React from 'react';
import {
  Lock,
  Calendar,
  FileSearch,
  Shield,
  Scale,
  Target,
  AlertTriangle,
} from 'lucide-react';
import { Button } from '@/components/ui/button';

/**
 * Aperçu verrouillé du LV3 (levier conversion n°1).
 *
 * Montre la STRUCTURE réelle de l'analyse payante avec des accroches chiffrées
 * personnalisées au dossier (issues du bloc LV3_teaser généré par l'Agent 2),
 * sans jamais révéler le contenu stratégique (gated côté serveur).
 *
 * Objectif : créer le curiosity gap qui déclenche l'achat impulsif à 9,99€.
 */

const Hook = ({ icon, label, value, locked }) => (
  <div className="flex items-start gap-3 p-3 bg-white rounded-lg border border-blue-100">
    <div className="mt-0.5 shrink-0" style={{ color: '#1e3a8a' }}>
      {icon}
    </div>
    <div className="flex-1 min-w-0">
      <p className="text-xs text-gray-500">{label}</p>
      <p className="font-semibold text-gray-900 flex items-center gap-2">
        <span className={locked ? 'tracking-wide' : ''}>{value}</span>
        {locked && <Lock size={13} className="text-gray-400 shrink-0" />}
      </p>
    </div>
  </div>
);

const LV3Preview = ({ teaser, onUnlock, paymentLoading, price = '9,99 €', offerLetter = false }) => {
  const t = teaser || {};
  const {
    prescription_date_trouvee,
    prescription_annee_indice,
    prescription_niveau_certitude,
    nb_actes_interruptifs = 0,
    nb_actes_contestables = 0,
    nb_axes_defense = 0,
    nb_arguments_juridiques = 0,
    nb_actions_recommandees = 0,
    nb_actions_urgentes = 0,
    enjeu_estime_eur,
    accroche,
  } = t;

  const enjeu =
    typeof enjeu_estime_eur === 'number' && enjeu_estime_eur >= 100
      ? new Intl.NumberFormat('fr-FR').format(enjeu_estime_eur)
      : null;

  const plural = (n) => (n > 1 ? 's' : '');

  // Construction des accroches chiffrées à partir des données réelles du dossier.
  const hooks = [];

  if (prescription_date_trouvee) {
    const annee = prescription_annee_indice || '••••';
    hooks.push({
      icon: <Calendar size={20} />,
      label: 'Date à partir de laquelle la dette ne serait plus réclamable en justice',
      value: `•• / •• / ${annee}`,
      locked: true,
    });
  }
  if (prescription_niveau_certitude) {
    hooks.push({
      icon: <Scale size={20} />,
      label: 'Fiabilité de cette estimation',
      value: prescription_niveau_certitude,
      locked: false,
    });
  }
  if (nb_actes_interruptifs > 0) {
    hooks.push({
      icon: <FileSearch size={20} />,
      label: 'Courriers ou actes qui ont pu remettre le compteur à zéro',
      value:
        nb_actes_contestables > 0
          ? `${nb_actes_interruptifs} trouvé${plural(nb_actes_interruptifs)}, dont ${nb_actes_contestables} peut-être invalide${plural(nb_actes_contestables)}`
          : `${nb_actes_interruptifs} trouvé${plural(nb_actes_interruptifs)}`,
      locked: true,
    });
  }
  if (nb_axes_defense > 0) {
    hooks.push({
      icon: <Shield size={20} />,
      label: 'Façons de vous défendre',
      value: `${nb_axes_defense} identifiée${plural(nb_axes_defense)}`,
      locked: true,
    });
  }
  if (nb_arguments_juridiques > 0) {
    hooks.push({
      icon: <Scale size={20} />,
      label: 'Arguments appuyés sur des textes de loi',
      value: `${nb_arguments_juridiques}`,
      locked: true,
    });
  }
  if (nb_actions_recommandees > 0) {
    hooks.push({
      icon: <Target size={20} />,
      label: 'Actions à faire, dans l\'ordre',
      value:
        nb_actions_urgentes > 0
          ? `${nb_actions_recommandees} dont ${nb_actions_urgentes} pressée${plural(nb_actions_urgentes)}`
          : `${nb_actions_recommandees}`,
      locked: true,
    });
  }

  // Fallback si le teaser est absent (anciens dossiers) : structure générique verrouillée.
  if (hooks.length === 0) {
    hooks.push(
      {
        icon: <Calendar size={20} />,
        label: 'Votre dette est-elle trop ancienne pour être réclamée ?',
        value: 'Vérification des dates et des délais légaux',
        locked: true,
      },
      {
        icon: <Shield size={20} />,
        label: 'Comment vous défendre',
        value: 'Les arguments qui s\'appliquent à votre dossier',
        locked: true,
      },
      {
        icon: <Target size={20} />,
        label: 'Quoi faire, dans quel ordre',
        value: 'Les étapes, avec les dates limites',
        locked: true,
      }
    );
  }

  return (
    <div
      className="card p-8 mb-8"
      style={{ background: 'linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)' }}
    >
      <div className="max-w-3xl mx-auto">
        {/* En-tête */}
        <div className="text-center mb-6">
          <h3 className="text-2xl font-bold mb-2" style={{ color: '#1e3a8a' }}>
            {offerLetter
              ? 'Votre courrier de demande de pièces est prêt'
              : 'Votre analyse complète est prête'}
          </h3>
          {offerLetter ? (
            <p className="text-lg text-gray-800 font-medium">
              Exigez du créancier qu'il prouve ce qu'il réclame — courrier sur-mesure, prêt à imprimer et poster.
            </p>
          ) : accroche ? (
            <p className="text-lg text-gray-800 font-medium">{accroche}</p>
          ) : (
            <p className="text-lg text-gray-700">
              Voici ce que notre IA a déjà trouvé dans votre dossier.
            </p>
          )}
        </div>

        {/* Bannière d'urgence (data-driven) */}
        {nb_actions_urgentes > 0 && (
          <div className="flex items-center gap-2 justify-center mb-6 px-4 py-3 rounded-lg bg-orange-50 border border-orange-200 text-orange-800">
            <AlertTriangle size={18} className="shrink-0" />
            <span className="text-sm font-semibold">
              {nb_actions_urgentes} action{plural(nb_actions_urgentes)} pressée
              {plural(nb_actions_urgentes)} détectée{plural(nb_actions_urgentes)} — il y a
              peut-être une date limite pour agir dans votre dossier.
            </span>
          </div>
        )}

        {/* Accroches chiffrées — ce que la machine a déjà trouvé */}
        <div className="grid sm:grid-cols-2 gap-3 mb-6">
          {hooks.map((h, i) => (
            <Hook key={i} {...h} />
          ))}
        </div>

        {/* Ancrage de valeur : enjeu du dossier vs prix de l'analyse */}
        {enjeu && (
          <div className="text-center mb-4 px-4 py-3 rounded-lg bg-green-50 border border-green-200">
            <p className="text-sm text-green-800">Enjeu estimé de votre dossier</p>
            <p className="text-2xl font-bold text-green-700">~{enjeu} €</p>
          </div>
        )}

        {/* CTA paiement */}
        <div className="text-center">
          <div className="bg-white rounded-lg p-6 mb-6 inline-block">
            <p className="text-4xl font-bold" style={{ color: '#1e3a8a' }}>
              {price} TTC
            </p>
            <p className="text-gray-600 mt-2">
              {offerLetter
                ? 'Votre courrier de demande de pièces, prêt à imprimer et poster (+ analyse complète)'
                : enjeu
                ? `Pour défendre un dossier dont l'enjeu est estimé à ${enjeu} €`
                : 'Recevez la stratégie et les actions à faire, étape par étape'}
            </p>
          </div>
          <div>
            <Button
              data-testid="payment-button"
              size="lg"
              onClick={onUnlock}
              disabled={paymentLoading}
              className="px-8 py-6 text-lg font-semibold"
              style={{ backgroundColor: '#1e3a8a', color: 'white' }}
            >
              {paymentLoading
                ? 'Redirection...'
                : offerLetter
                ? 'Débloquer mon courrier prêt à poster'
                : 'Débloquer mon analyse complète'}
            </Button>
          </div>
          <p className="text-sm text-gray-500 mt-4">🔒 Paiement sécurisé par Stripe</p>
        </div>
      </div>
    </div>
  );
};

export default LV3Preview;
